// =============================================================================
// io/storage/recorder.ts — microphone capture for voice notes.
// -----------------------------------------------------------------------------
// Ported from the old client's `startRecording` / `stopRecording`
// (index.html:17892). In `io/` and not `ui/` because it is `navigator`,
// `MediaRecorder` and a stream — no React, and the same layer as the upload it
// feeds.
//
// ── 🚨 EVERY FAILURE RETURNS A SENTENCE, NOT A THROW ──
//
// Five distinct things go wrong with a microphone and beta names all five,
// because "recording failed" is useless to someone whose mic is muted in
// Windows. That mapping is reproduced verbatim — it is the accumulated result of
// Josh actually hitting these, not a defensive checklist.
//
// ── 🚨 THE 500-BYTE FLOOR IS A REAL BUG FIX, NOT A HEURISTIC ──
//
// An empty WebM/Opus container is ~110 bytes. Stop within a few hundred ms of
// Record — or record with a muted input — and you get a well-formed audio file
// containing no audio, which uploads cleanly, attaches cleanly, and plays
// silence forever. Beta's own comment says so. Rejecting it here is the only
// place it can be caught, because every layer above sees a valid Blob.
//
// ── THE STREAM IS ALWAYS RELEASED ──
//
// `getTracks().forEach(stop)` on the way out, in `onstop`. A live audio track
// leaves the browser's recording indicator lit and holds the device against
// other apps — which is one of the five errors above, self-inflicted.
// =============================================================================

import { reportFailureAs } from '../diagnostics/errorLog';

/** Preference order. WebM/Opus first — smallest, and what beta has been writing. */
const PREFERRED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

/** Below this a recording is a container with no audio in it. See the header. */
export const MIN_RECORDING_BYTES = 500;

export interface RecorderHandle {
  /** Stops capture. The `onStop` passed to `startRecording` fires after it. */
  stop: () => void;
}

export interface StartRecordingArgs {
  /** Called once, with the finished audio. Not called for a rejected recording. */
  onStop: (blob: Blob) => void;
  /** Called instead of `onStop` when the recording is unusable. */
  onError: (message: string) => void;
}

/**
 * Beta's five named cases. Anything else falls through with its own name.
 *
 * ⚠ BUILDS THE SENTENCE, DOES NOT RECORD IT. The log entry is made where the
 * sentence is handed to `onError`, so one failure is one line — moving the
 * funnel in here would log a case that was only being formatted.
 */
function micErrorMessage(e: unknown): string {
  const name = e instanceof Error ? e.name : '';
  const message = e instanceof Error ? e.message : String(e);
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Mic permission denied. Allow it for this page from the address bar, then retry.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone detected. Check Windows sound settings → Input.';
    case 'NotReadableError':
      return 'The mic is in use by another app. Close it and retry.';
    case 'SecurityError':
      return 'Mic blocked by browser security policy on this URL.';
    default:
      return `${name || 'Error'}: ${message}`;
  }
}

/**
 * Begin recording. Resolves with a handle, or `null` after calling `onError`.
 *
 * ⚠ Returns null rather than throwing so the caller's button state is a plain
 * `if`. A throw here would have to be caught in a click handler that also has
 * to un-set "Recording…", and that is the shape that leaves a UI stuck.
 */
export async function startRecording(
  { onStop, onError }: StartRecordingArgs,
): Promise<RecorderHandle | null> {
  if (!navigator.mediaDevices?.getUserMedia) {
    onError('This browser has no microphone API. Recording needs a recent Chrome or Edge.');
    return null;
  }
  if (typeof MediaRecorder === 'undefined') {
    onError('This browser has no MediaRecorder API.');
    return null;
  }

  const mime = PREFERRED_MIMES.find((m) => MediaRecorder.isTypeSupported?.(m)) ?? '';

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    // ⚠ The mapped sentence is what gets logged, not `String(e)`. A denied mic
    // throws a bare `DOMException`, so the name of the case is the evidence.
    onError(reportFailureAs('microphone', e, micErrorMessage(e)));
    return null;
  }

  try {
    const rec = mime
      ? new MediaRecorder(stream, { mimeType: mime })
      : new MediaRecorder(stream);
    const chunks: Blob[] = [];

    rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    rec.onerror = () => onError('The recorder stopped unexpectedly.');
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
      // ⚠ Released HERE, on every path out. See the header.
      stream.getTracks().forEach((t) => t.stop());

      if (blob.size === 0) { onError('The recording produced no data — try again.'); return; }
      if (blob.size < MIN_RECORDING_BYTES) {
        onError(
          'Nothing was captured. Hold the recording for at least a second, and check ' +
          'the Windows mic input is not muted.',
        );
        return;
      }
      onStop(blob);
    };

    // ⚠ 250 ms timeslice, beta's. Without it `ondataavailable` fires only at the
    // end, so a recording that is silently failing looks identical to one that
    // is working until the moment you stop it.
    rec.start(250);
    return { stop: () => { if (rec.state === 'recording') rec.stop(); } };
  } catch (e) {
    stream.getTracks().forEach((t) => t.stop());
    onError(reportFailureAs('start recording', e, micErrorMessage(e)));
    return null;
  }
}
